import { useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { ALL_ADMIN_ZONES, type AdminZone } from "@/lib/rbac";
import { cn } from "@/lib/utils";
import { Shield, Users, Briefcase, Building2 } from "lucide-react";

const ZONE_ICONS: Record<AdminZone, React.ReactNode> = {
  admin: <Shield className="h-4 w-4" />,
  app: <Users className="h-4 w-4" />,
  partner: <Briefcase className="h-4 w-4" />,
  enterprise: <Building2 className="h-4 w-4" />,
};

const ZONE_DESCRIPTIONS: Record<AdminZone, string> = {
  admin: "System-wide management & monitoring",
  app: "Browse the consumer application",
  partner: "View the partner portal",
  enterprise: "Manage organizations & SSO",
};

/**
 * Admin Zone Selector — allows platform_admin users to choose which
 * application zone they want to access. Only rendered for admins.
 *
 * Can be used as a standalone page section or dropped into any admin layout.
 */
export function AdminZoneSelector({ compact = false }: { compact?: boolean }) {
  const { isAdmin, adminSelectedZone, setAdminSelectedZone } = useSession();
  const navigate = useNavigate();

  if (!isAdmin) return null;

  const handleZoneSelect = (zone: AdminZone) => {
    setAdminSelectedZone(zone);
    const target = ALL_ADMIN_ZONES.find(z => z.zone === zone);
    if (target) {
      navigate(target.path);
    }
  };

  if (compact) {
    return (
      <div className="space-y-1">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Switch Zone
        </span>
        <div className="grid grid-cols-2 gap-1.5">
          {ALL_ADMIN_ZONES.map((z) => (
            <button
              key={z.zone}
              onClick={() => handleZoneSelect(z.zone)}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors",
                adminSelectedZone === z.zone
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
              )}
            >
              {ZONE_ICONS[z.zone]}
              {z.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Zone Access</h3>
        <p className="text-xs text-muted-foreground mt-1">
          As a platform admin, you can access any area of the application.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {ALL_ADMIN_ZONES.map((z) => (
          <button
            key={z.zone}
            onClick={() => handleZoneSelect(z.zone)}
            className={cn(
              "flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-all",
              adminSelectedZone === z.zone
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-primary/50 hover:bg-muted/50"
            )}
          >
            <div className="flex items-center gap-2">
              {ZONE_ICONS[z.zone]}
              <span className="text-sm font-medium">{z.label}</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {ZONE_DESCRIPTIONS[z.zone]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
